import React from "react";
import {
  FiSearch,
  FiUser,
  FiPhone,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { MdPets, MdShoppingCart } from "react-icons/md";
import { FaRuler } from "react-icons/fa";
import { HiArrowSmLeft } from "react-icons/hi";

const EnquiryListDrawer = ({
  setShowEnquiryDrawer,
  clientData,
  enquiryData,
  loadingEnquiries,
  expandedEnquiry,
  setExpandedEnquiry,
  handleDeleteEnquiry,
  handleEditEnquiry,
}) => {
  const getEnquirySummary = (items = []) => {
    const services = items.filter((i) => i.type !== "product");
    const products = items.filter((i) => i.type === "product");

    const addonsCount = items.reduce(
      (acc, item) => acc + (item?.addons?.length || 0),
      0,
    );

    return {
      servicesCount: services.length,
      productsCount: products.length,
      addonsCount,
    };
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => setShowEnquiryDrawer(false)}
      />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full lg:w-2/5 bg-gray-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setShowEnquiryDrawer(false)}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition"
          >
            <HiArrowSmLeft className="w-5 h-5 text-gray-700" />
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <FiSearch className="text-white w-4 h-4" />
            </div>

            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Enquiries
              </h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-3 space-y-3">
          {/* Client */}
          {clientData && (
            <div className="bg-white border rounded-xl px-3 py-2.5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FiUser className="w-4 h-4 text-indigo-600" />
                </div>

                <div className="min-w-0">
                  <h3 className="font-medium text-sm text-gray-900 truncate">
                    {clientData.firstname} {clientData.lastname}
                  </h3>

                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <FiPhone className="w-3 h-3" />
                    {clientData.phone}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loadingEnquiries ? (
            <div className="py-16 flex flex-col items-center justify-center">
              <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-indigo-600 animate-spin" />

              <p className="mt-3 text-gray-500 text-sm">
                Loading enquiries...
              </p>
            </div>
          ) : enquiryData?.length === 0 ? (
            <div className="bg-white border rounded-xl py-12 px-5 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <FiSearch className="w-6 h-6 text-gray-400" />
              </div>

              <h3 className="mt-4 font-medium text-gray-800">
                No enquiries found
              </h3>

              <p className="text-xs text-gray-500 mt-1">
                This client has no enquiry history yet.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {enquiryData.map((enquiry, index) => {
                const summary = getEnquirySummary(enquiry?.items || []);
                const isExpanded = expandedEnquiry === enquiry._id;

                return (
                  <div
                    key={enquiry._id || index}
                    className="bg-white border rounded-xl shadow-sm overflow-hidden"
                  >
                    {/* Main Card */}
                    <div
                      onClick={() =>
                        setExpandedEnquiry(isExpanded ? null : enquiry._id)
                      }
                      className="cursor-pointer px-3 py-3"
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          {/* Top Row */}
                          <div className="flex items-start justify-between gap-3">
                            {/* Left */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold whitespace-nowrap">
                                  #{enquiry.order_number}
                                </div>

                                <div className="flex items-center gap-1.5 flex-wrap text-gray-500">
                                  <span className="text-[11px]">
                                    {new Date(
                                      enquiry.createdAt,
                                    ).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>

                                  {summary.servicesCount > 0 && (
                                    <>
                                      <span className="text-[11px]">•</span>

                                      <span className="text-[11px] text-orange-600 font-medium whitespace-nowrap">
                                        {summary.servicesCount} Service
                                        {summary.servicesCount > 1 ? "s" : ""}
                                      </span>
                                    </>
                                  )}

                                  {summary.productsCount > 0 && (
                                    <>
                                      <span className="text-[11px]">•</span>

                                      <span className="text-[11px] text-green-600 font-medium whitespace-nowrap">
                                        {summary.productsCount} Product
                                        {summary.productsCount > 1 ? "s" : ""}
                                      </span>
                                    </>
                                  )}

                                  {summary.addonsCount > 0 && (
                                    <>
                                      <span className="text-[11px]">•</span>

                                      <span className="text-[11px] text-purple-600 font-medium whitespace-nowrap">
                                        {summary.addonsCount} Addon
                                        {summary.addonsCount > 1 ? "s" : ""}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditEnquiry(enquiry);
                                }}
                                className="w-8 h-8 rounded-lg border hover:bg-indigo-50 hover:border-indigo-200 flex items-center justify-center transition"
                              >
                                <FiEdit2 className="w-3.5 h-3.5 text-indigo-600" />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEnquiry(enquiry);
                                }}
                                className="w-8 h-8 rounded-lg border hover:bg-red-50 hover:border-red-200 flex items-center justify-center transition"
                              >
                                <FiTrash2 className="w-3.5 h-3.5 text-red-500" />
                              </button>
                            </div>
                          </div>

                          {/* Preview */}
                          <div
                            className={`mt-3 flex flex-wrap gap-3 ${
                              enquiry?.items?.length === 1 ? "flex-col" : ""
                            }`}
                          >
                            {enquiry?.items
                              ?.slice(0, isExpanded ? undefined : 2)
                              ?.map((item, itemIndex) => (
                                <div
                                  key={item._id || itemIndex}
                                  className={`flex items-start gap-2.5 min-w-0 ${
                                    enquiry?.items?.length > 1
                                      ? "flex-1 basis-[48%]"
                                      : "w-full"
                                  }`}
                                >
                                  {/* Icon */}
                                  <div
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      item.type === "product"
                                        ? "bg-green-100"
                                        : "bg-orange-100"
                                    }`}
                                  >
                                    {item.type === "product" ? (
                                      <MdShoppingCart className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <MdPets className="w-4 h-4 text-orange-600" />
                                    )}
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    {item.type === "product" ? (
                                      <>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <h4 className="font-medium text-sm text-gray-900 truncate">
                                            {item.service_name ||
                                              item.product_details?.name}
                                          </h4>

                                          <span className="text-[11px] text-gray-500 whitespace-nowrap">
                                            × {item.quantity || 1}
                                          </span>
                                        </div>

                                        {item.sku_number && (
                                          <p className="text-[11px] text-gray-400 truncate mt-0.5">
                                            SKU: {item.sku_number}
                                          </p>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <h4 className="font-medium text-sm text-gray-900 truncate">
                                            {item.pet_name}
                                          </h4>

                                          {item.pet_breed && (
                                            <span className="text-[11px] text-gray-500 truncate">
                                              • {item.pet_breed}
                                            </span>
                                          )}

                                          {item.pet_size && (
                                            <span className="text-[11px] text-gray-500 flex items-center gap-1 whitespace-nowrap">
                                              •
                                              <FaRuler className="w-2.5 h-2.5" />
                                              {item.pet_size}
                                            </span>
                                          )}
                                        </div>

                                        <p className="text-[12px] text-gray-700 truncate">
                                          {item.service_name || "Service"}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500 mt-0.5">
                                          {(item.start_date ||
                                            item.appointment_date) && (
                                            <span>
                                              {new Date(
                                                item.start_date ||
                                                  item.appointment_date,
                                              ).toLocaleDateString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                              })}
                                            </span>
                                          )}

                                          {item.time_slot && (
                                            <span>• {item.time_slot}</span>
                                          )}

                                          {item.employee_name && (
                                            <span className="truncate">
                                              • {item.employee_name}
                                            </span>
                                          )}
                                        </div>
                                      </>
                                    )}

                                    {/* Addons */}
                                    {item?.addons?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        {item.addons
                                          .slice(0, 2)
                                          .map((addon, addonIndex) => (
                                            <div
                                              key={addon._id || addonIndex}
                                              className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-medium truncate max-w-[100px]"
                                            >
                                              {addon.name}
                                            </div>
                                          ))}

                                        {item.addons.length > 2 && (
                                          <div className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium">
                                            +{item.addons.length - 2}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}

                            {/* More Items */}
                            {!isExpanded && enquiry?.items?.length > 2 && (
                              <div className="pl-10 text-[11px] text-indigo-600 font-medium">
                                +{enquiry.items.length - 2} more item
                                {enquiry.items.length - 2 > 1 ? "s" : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryListDrawer;